import { FC, useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { Input, Button, CopyableInput, Select, ISelectOptionProp } from '../components/Form';
import { SortableHeader } from '../components/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  OrganizationBase,
  OrganizationDetail,
  OrganizationCreateUpdate,
  SubscriptionSettings, TConversationProvider,
} from '../api/api';

const OrganizationForm: FC<{
  organization?: OrganizationDetail;
  onSubmit: (data: OrganizationCreateUpdate) => void;
  onCancel: () => void;
}> = ({ organization, onSubmit, onCancel }) => {
  const conversation_provider_options: ISelectOptionProp[] = [
    { _id: 'ultravox', name: 'ultravox', },
    { _id: 'heygen', name: 'heygen', },
  ];

  const [formData, setFormData] = useState<OrganizationCreateUpdate>(() => ({
    name: organization?.name || '',
    trial_available: organization?.trial_available ?? true,
    conversation_provider: organization?.conversation_provider ?? 'ultravox',
    subscription_settings: {
      show_lite_plan: organization?.subscription_settings?.show_lite_plan ?? true,
      show_advanced_plan: organization?.subscription_settings?.show_advanced_plan ?? true,
      show_enterprise_plan: organization?.subscription_settings?.show_enterprise_plan ?? true,
      allow_annual_payment: organization?.subscription_settings?.allow_annual_payment ?? true,
      allow_monthly_payment: organization?.subscription_settings?.allow_monthly_payment ?? true,
      default_payment_option: organization?.subscription_settings?.default_payment_option ?? 'monthly',
    },
  }));

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        trial_available: organization.trial_available ?? true,
        conversation_provider: organization?.conversation_provider ?? 'ultravox',
        subscription_settings: {
          show_lite_plan: organization.subscription_settings?.show_lite_plan ?? true,
          show_advanced_plan: organization.subscription_settings?.show_advanced_plan ?? true,
          show_enterprise_plan: organization.subscription_settings?.show_enterprise_plan ?? true,
          allow_annual_payment: organization.subscription_settings?.allow_annual_payment ?? true,
          allow_monthly_payment: organization.subscription_settings?.allow_monthly_payment ?? true,
          default_payment_option: organization.subscription_settings?.default_payment_option ?? 'monthly',
        },
      });
    }
  }, [organization]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateSubscriptionSetting = (key: keyof SubscriptionSettings, value: boolean | 'monthly' | 'annual') => {
    setFormData(prev => ({
      ...prev,
      subscription_settings: {
        ...prev.subscription_settings,
        [key]: value,
      },
    }));
  };

  const updatesConversationProvider = (conversationProvider: TConversationProvider) => {
    setFormData((prev) => ({
      ...prev,
      conversation_provider: conversationProvider,
    }));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {organization && (
        <CopyableInput
          label="ID"
          value={organization._id}
        />
      )}
      <Input
        label="Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
      />
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="trial_available"
          checked={formData.trial_available}
          onChange={(e) =>
            setFormData({ ...formData, trial_available: e.target.checked })
          }
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label
          htmlFor="trial_available"
          className="text-sm font-medium text-gray-700"
        >
          Trial Available
        </label>
      </div>

      <div className="space-y-6 pt-4 border-t border-gray-200">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI Options</h3>

          <Select label="Conversation provider"
                  value={formData.conversation_provider}
                  onChange={(e) => updatesConversationProvider(e.target.value as TConversationProvider)}
                  options={conversation_provider_options}
                  showEmptyOption={false}>
          </Select>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Plan Visibility Flags</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show_lite_plan"
                checked={formData.subscription_settings.show_lite_plan}
                onChange={(e) => updateSubscriptionSetting('show_lite_plan', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="show_lite_plan" className="text-sm font-medium text-gray-700">
                Show Lite Subscription Plan
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show_advanced_plan"
                checked={formData.subscription_settings.show_advanced_plan}
                onChange={(e) => updateSubscriptionSetting('show_advanced_plan', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="show_advanced_plan" className="text-sm font-medium text-gray-700">
                Show Advanced Subscription Plan
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show_enterprise_plan"
                checked={formData.subscription_settings.show_enterprise_plan}
                onChange={(e) => updateSubscriptionSetting('show_enterprise_plan', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="show_enterprise_plan" className="text-sm font-medium text-gray-700">
                Show Enterprise Subscription Plan
              </label>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Type Availability</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allow_annual_payment"
                checked={formData.subscription_settings.allow_annual_payment}
                onChange={(e) => updateSubscriptionSetting('allow_annual_payment', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="allow_annual_payment" className="text-sm font-medium text-gray-700">
                Allow Annual Payment (12 months)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allow_monthly_payment"
                checked={formData.subscription_settings.allow_monthly_payment}
                onChange={(e) => updateSubscriptionSetting('allow_monthly_payment', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="allow_monthly_payment" className="text-sm font-medium text-gray-700">
                Allow Monthly Payment
              </label>
            </div>
          </div>
        </div>

        {formData.subscription_settings.allow_annual_payment || formData.subscription_settings.allow_monthly_payment ? (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Default Payment Display Option</h3>
            <Select
              label="Default Payment Option"
              value={formData.subscription_settings.default_payment_option}
              onChange={(e) => updateSubscriptionSetting('default_payment_option', e.target.value as 'monthly' | 'annual')}
              options={[
                ...(formData.subscription_settings.allow_monthly_payment ? [{ _id: 'monthly', name: 'Monthly' }] : []),
                ...(formData.subscription_settings.allow_annual_payment ? [{ _id: 'annual', name: 'Annual' }] : []),
              ]}
              showEmptyOption={false}
            />
          </div>
        ) : null}
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
        <Button type="button" onClick={onCancel} className="bg-gray-600">
          Cancel
        </Button>
        <Button type="submit">{organization ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
};

export const Organizations: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationType, setOrganizationType] = useState('real');

  const { data: organizations, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery();
  const { data: selectedOrganization, isLoading: isLoadingOrganization } = useGetOrganizationQuery(
    selectedOrganizationId ?? '',
    { skip: !selectedOrganizationId }
  );
  const [createOrganization] = useCreateOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();

  // Filter organizations based on search term
  const filteredOrganizations = organizations?.filter((org) =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    ((org.is_virtual && organizationType === 'virtual') ||
    (!org.is_virtual && organizationType === 'real') || organizationType === 'all')
  );

  useEffect(() => {
    console.log('Organization type changed:', organizationType);
  }, [organizationType]);

  const { items: sortedOrganizations, requestSort, sortConfig } = useSorting<OrganizationBase>(
    filteredOrganizations || []
  );

  const handleSubmit = async (data: OrganizationCreateUpdate) => {
    try {
      if (selectedOrganizationId) {
        await updateOrganization({ id: selectedOrganizationId, body: data });
      } else {
        await createOrganization(data);
      }
      setIsModalOpen(false);
      setSelectedOrganizationId(undefined);
    } catch (error) {
      console.error('Error saving organization:', error);
    }
  };

  if (isLoadingOrganizations) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Organizations</h1>
        <Button onClick={() => setIsModalOpen(true)}>Create Organization</Button>
      </div>

      <div className="w-full flex flex-row gap-4 mb-6">
        <Input
          label="Search"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className=""
        />
        <Select
          label="Organization Type"
          className=''
          value={organizationType}
          onChange={(e) => {
            setOrganizationType(e.target.value);
          }}
          showEmptyOption={false}
          options={[
            { _id: 'all', name: 'All' },
            { _id: 'real', name: 'Real' },
            { _id: 'virtual', name: 'Virtual' },]}
        />
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader<OrganizationBase>
                label="Name"
                sortKey="name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trial Available
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Is Virtual
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedOrganizations?.map((organization) => (
              <tr
                key={organization._id}
                onClick={() => {
                  setSelectedOrganizationId(organization._id);
                  setIsModalOpen(true);
                }}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">{organization.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {organization.trial_available ? 'Yes' : 'No'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {organization.is_virtual ? 'Yes' : 'No'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedOrganizationId(undefined);
        }}
        title={selectedOrganizationId ? 'Edit Organization' : 'Create Organization'}
      >
        {isLoadingOrganization ? (
          <div>Loading organization details...</div>
        ) : (
          <OrganizationForm
            organization={selectedOrganization}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setSelectedOrganizationId(undefined);
            }}
          />
        )}
      </Modal>
    </div>
  );
};
